import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { catchError, map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class OptimizedI18nService {
  private currentLanguageSubject = new BehaviorSubject<string>('es');
  public currentLanguage$ = this.currentLanguageSubject.asObservable();
  
  private translationsCache: { [lang: string]: { [key: string]: string } } = {};
  private translationsSubject = new BehaviorSubject<{ [key: string]: string }>({});
  public translations$ = this.translationsSubject.asObservable();
  
  // Idioma base (español) - siempre cargado
  private baseTranslations: { [key: string]: string } = {
    // === NAVEGACIÓN BÁSICA ===
    'nav.home': 'Inicio',
    'nav.products': 'Productos',
    'nav.categories': 'Categorías',
    'nav.login': 'Iniciar Sesión',
    'nav.register': 'Registrarse',
    'nav.profile': 'Perfil',
    'nav.logout': 'Cerrar Sesión',
    'nav.cart': 'Carrito',
    'nav.search': 'Buscar productos...',
    
    // === BOTONES BÁSICOS ===
    'btn.add_to_cart': 'Agregar al Carrito',
    'btn.buy_now': 'Comprar Ahora',
    'btn.save': 'Guardar',
    'btn.cancel': 'Cancelar',
    'btn.edit': 'Editar',
    'btn.delete': 'Eliminar',
    'btn.confirm': 'Confirmar',
    'btn.back': 'Volver',
    'btn.sign_in': 'Iniciar Sesión',
    'btn.next': 'Siguiente',
    'btn.submit': 'Enviar',
    'btn.search': 'Buscar',
    'btn.clear': 'Limpiar',
    'btn.close': 'Cerrar',
    'btn.continue_shopping': 'Continuar Comprando',
    'btn.contact_us': 'Contáctanos',
    
    // === PRODUCTOS BÁSICOS ===
    'product.title': 'Producto',
    'product.name': 'Nombre',
    'product.price': 'Precio',
    'product.description': 'Descripción',
    'product.category': 'Categoría',
    'product.stock': 'Stock',
    'product.in_stock': 'En Stock',
    'product.out_of_stock': 'Sin Stock',
    'product.quantity': 'Cantidad',
    'product.total': 'Total',
    'product.subtotal': 'Subtotal',
    'product.discount': 'Descuento',
    
    // === FORMULARIOS ADICIONALES ===
    'form.phone_or_email': 'Número de celular o correo electrónico',
    'form.phone_or_email_placeholder': 'Ingresa tu correo o número de celular',
    'form.forgot_password': '¿Olvidaste tu contraseña?',
    'form.no_account': '¿No tienes cuenta?',
    'form.write_here': 'Escribe aquí',
    'form.custom_name': 'Nombre personalizado',
    'form.department': 'Departamento',
    'form.city': 'Ciudad',
    'form.address': 'Dirección',
    'form.select_option': 'Seleccione una opción',
    'form.accept_terms': 'Acepto los',
    'form.change_password': 'Cambiar contraseña',
    'form.delete_account': 'Eliminar cuenta',
    
    // === CARRITO BÁSICO ===
    'cart.title': 'Carrito de Compras',
    'cart.empty': 'Tu carrito está vacío',
    'cart.continue_shopping': 'Continuar Comprando',
    'cart.checkout': 'Finalizar Compra',
    'cart.remove_item': 'Eliminar Artículo',
    
    // === CHECKOUT BÁSICO ===
    'checkout.title': 'Finalizar Compra',
    'checkout.billing_info': 'Información de Facturación',
    'checkout.shipping_info': 'Información de Envío',
    'checkout.payment_method': 'Método de Pago',
    'checkout.order_summary': 'Resumen del Pedido',
    'checkout.first_name': 'Nombre',
    'checkout.last_name': 'Apellido',
    'checkout.email': 'Correo Electrónico',
    'checkout.phone': 'Teléfono',
    'checkout.address': 'Dirección',
    'checkout.city': 'Ciudad',
    'checkout.country': 'País',
    
    // === PEDIDOS ===
    'order.title': 'Tus pedidos',
    'order.pending': 'Pendientes',
    'order.in_progress': 'En curso',
    'order.delivered': 'Entregados',
    'order.cancelled': 'Cancelados',
    'order.write_review': 'Escribir reseña',
    'order.no_orders': 'Aún no has realizado ningún pedido',
    
    // === PÁGINAS ===
    'page.about': 'Quiénes Somos',
    'page.search_results': 'Resultados de búsqueda',
    'page.featured_products': 'Productos destacados',
    'page.see_more': 'Ver más',

    'page.clear_filters': 'Limpiar filtros',
    'page.our_stores': 'Nuestras tiendas',
    'page.results_for': 'Resultados para',
    'page.showing': 'Mostrando',
    'page.of': 'de',
    'page.result': 'resultado',
    'page.results': 'resultados',
    
    // === MODALES ===
    'modal.shipping_policy': 'Política de envío',
    'modal.add_address': 'Agregar dirección',
    'modal.edit': 'Editar',
    'modal.delete': 'Eliminar',
    'modal.add': 'Agregar',
    'modal.save_address': 'Guardar dirección',
    
    // === PRODUCTOS ===
    'product.size': 'Talla',
    'product.color': 'Color',
    'product.coordinate_shipping': 'Coordinar envío',
    'product.pickup_store': 'Recoge en tienda',
    'product.pay_pickup': 'Paga y recoge en tienda',
    'product.sold_out': 'Agotado',
    'product.slogan': 'Compra con confianza. Todos tus pagos están protegidos y verificados.',

    
    // === CHECKOUT ADICIONAL ===
    'checkout.delivery_data': 'Datos de entrega',
    'checkout.billing_data': 'Datos de facturación',
    'checkout.use_same_data': 'Usar los mismos datos de entrega',
    'checkout.delivery_options': 'Opciones de entrega',
    'checkout.metropolitan_delivery': 'Entrega Área metropolitana y alrededores',
    'checkout.national_delivery': 'Entrega nacional',
    'checkout.main_cities': 'Ciudades principales: 2 a 3 días hábiles y otros municipios: 5 días hábiles o más. El tiempo exacto depende de la transportadora.',
    'checkout.price_to_calculate': 'Precio por calcular',
    'checkout.payment': 'Pago',
    'checkout.purchase_summary': 'Resumen de compra',
    'checkout.view_shipping_policies': 'Ver políticas de envío',
    
    // === USUARIO BÁSICO ===
    'user.profile': 'Perfil de Usuario',
    'user.personal_info': 'Información Personal',
    'user.change_password': 'Cambiar Contraseña',
    'user.current_password': 'Contraseña Actual',
    'user.new_password': 'Nueva Contraseña',
    'user.confirm_password': 'Confirmar Contraseña',

    // === PEDIDOS BÁSICOS ===
    'order.order_number': 'Número de Pedido',
    'order.order_date': 'Fecha del Pedido',
    'order.order_status': 'Estado del Pedido',
    'order.order_total': 'Total del Pedido',
    'order.processing': 'Procesando',
    'order.shipped': 'Enviado',
    
    // === MENSAJES BÁSICOS ===
    'message.success': '¡Éxito!',
    'message.error': 'Error',
    'message.warning': 'Advertencia',
    'message.info': 'Información',
    'message.loading': 'Cargando...',
    'message.no_results': 'No se encontraron resultados',
    'message.try_again': 'Inténtalo de nuevo',
    'message.connection_error': 'Error de conexión',
    'message.invalid_credentials': 'Credenciales inválidas',
    'message.account_created': 'Cuenta creada exitosamente',
    'message.login_success': 'Inicio de sesión exitoso',
    'message.logout_success': 'Sesión cerrada exitosamente',
    'message.product_added_to_cart': 'Producto agregado al carrito',
    'message.product_removed_from_cart': 'Producto eliminado del carrito',
    'message.order_placed': 'Pedido realizado exitosamente',
    'message.payment_successful': 'Pago realizado exitosamente',
    
    // === FORMULARIOS BÁSICOS ===
    'form.required': 'Este campo es obligatorio',
    'form.password': 'Contraseña',
    'form.first_name': 'Nombre',
    'form.last_name': 'Apellido',
    'form.invalid_email': 'Correo electrónico inválido',
    'form.password_too_short': 'La contraseña es muy corta',
    'form.passwords_dont_match': 'Las contraseñas no coinciden',
    'form.invalid_phone': 'Número de teléfono inválido',
    'form.terms_required': 'Debes aceptar los términos y condiciones',
    
    // === FOOTER BÁSICO ===
    'footer.about_us': 'Acerca de Nosotros',
    'footer.contact_us': 'Contáctanos',
    'footer.customer_service': 'Servicio al Cliente',
    'footer.shipping_info': 'Información de Envío',
    'footer.return_policy': 'Política de Devoluciones',
    'footer.privacy_policy': 'Política de Privacidad',
    'footer.terms_conditions': 'Términos y Condiciones',
    'footer.follow_us': 'Síguenos',
    'footer.copyright': 'Todos los derechos reservados',
    'footer.text_colombian': 'Orgullosamente Colombianos',

    
    // === PÁGINAS BÁSICAS ===
    'page.home': 'Inicio',
    'page.contact': 'Contacto',
    'page.login': 'Iniciar Sesión',
    'page.register': 'Registrarse',
    'page.forgot_password': 'Olvidé mi Contraseña',
    'page.reset_password': 'Restablecer Contraseña',
    'page.verify_email': 'Verificar Email',
    'page.not_found': 'Página No Encontrada',
    'page.access_denied': 'Acceso Denegado',
    'page.server_error': 'Error del Servidor',
    'page.checkout': 'Proceso de Compra',
    'page.terms': 'Términos y Condiciones',
    'page.privacy': 'Política de Privacidad',
    'page.services': 'Servicios',
    'page.providers': 'Proveedores',
    'page.categories': 'Categorías',
    'page.products': 'Productos',
    
    // === FILTROS BÁSICOS ===
    'filter.title': 'Filtros',
    'filter.price_range': 'Rango de Precio',
    'filter.brand': 'Marca',
    'filter.rating': 'Calificación',
    'filter.availability': 'Disponibilidad',
    'filter.sort_by': 'Ordenar por',
    'filter.price_low_high': 'Precio: Menor a Mayor',
    'filter.price_high_low': 'Precio: Mayor a Menor',
    'filter.name_a_z': 'Nombre: A-Z',
    'filter.name_z_a': 'Nombre: Z-A',
    'filter.newest': 'Más Recientes',
    'filter.popular': 'Más Populares',
    'filter.clear_all': 'Limpiar Todo',
    
    // === VENDEDORES BÁSICOS ===
    'seller.title': 'Vendedor',
    'seller.shop': 'Tienda',
    'seller.rating': 'Calificación',
    'seller.reviews': 'Reseñas',
    'seller.products': 'Productos',
    'seller.contact': 'Contactar',
    'seller.visit_shop': 'Visitar Tienda',
    'seller.about': 'Acerca del Vendedor',
    
  };

  // Traducciones en inglés (cargadas dinámicamente)
  private englishTranslations: { [key: string]: string } = {
    // === BASIC NAVIGATION ===
    'nav.home': 'Home',
    'nav.products': 'Products',
    'nav.categories': 'Categories',
    'nav.login': 'Login',
    'nav.register': 'Register',
    'nav.profile': 'Profile',
    'nav.logout': 'Logout',
    'nav.cart': 'Cart',
    'nav.search': 'Search products...',
    
    // === BASIC BUTTONS ===
    'btn.add_to_cart': 'Add to Cart',
    'btn.buy_now': 'Buy Now',
    'btn.save': 'Save',
    'btn.cancel': 'Cancel',
    'btn.edit': 'Edit',
    'btn.delete': 'Delete',
    'btn.confirm': 'Confirm',
    'btn.back': 'Back',
    'btn.next': 'Next',
    'btn.sign_in': 'Sign In',
    'btn.submit': 'Submit',
    'btn.search': 'Search',
    'btn.clear': 'Clear',
    'btn.close': 'Close',
    'btn.continue_shopping': 'Continue Shopping',
    'btn.contact_us': 'Contact Us',
    
    // === BASIC PRODUCTS ===
    'product.title': 'Product',
    'product.name': 'Name',
    'product.price': 'Price',
    'product.description': 'Description',
    'product.category': 'Category',
    'product.stock': 'Stock',
    'product.in_stock': 'In Stock',
    'product.out_of_stock': 'Out of Stock',
    'product.quantity': 'Quantity',
    'product.total': 'Total',
    'product.subtotal': 'Subtotal',
    'product.discount': 'Discount',
    'product.coordinate_shipping': 'Coordinate shipping',
    'product.pickup_store': 'Pickup in store',
    'product.pay_pickup': 'Pay and pickup in store',
    'product.slogan': 'Buy with confidence. All your payments are protected and verified.',
    // === BASIC CART ===
    'cart.title': 'Shopping Cart',
    'cart.empty': 'Your cart is empty',
    'cart.continue_shopping': 'Continue Shopping',
    'cart.checkout': 'Checkout',
    'cart.remove_item': 'Remove Item',
    
    // === BASIC CHECKOUT ===
    'checkout.title': 'Checkout',
    'checkout.billing_info': 'Billing Information',
    'checkout.shipping_info': 'Shipping Information',
    'checkout.payment_method': 'Payment Method',
    'checkout.order_summary': 'Order Summary',
    'checkout.first_name': 'First Name',
    'checkout.last_name': 'Last Name',
    'checkout.email': 'Email',
    'checkout.phone': 'Phone',
    'checkout.address': 'Address',
    'checkout.city': 'City',
    'checkout.country': 'Country',
    
    // === BASIC USER ===
    'user.profile': 'User Profile',
    'user.personal_info': 'Personal Information',
    'user.change_password': 'Change Password',
    'user.current_password': 'Current Password',
    'user.new_password': 'New Password',
    'user.confirm_password': 'Confirm Password',
    
    // === BASIC ORDERS ===
    'order.title': 'Orders',
    'order.order_number': 'Order Number',
    'order.order_date': 'Order Date',
    'order.order_status': 'Order Status',
    'order.order_total': 'Order Total',
    'order.pending': 'Pending',
    'order.processing': 'Processing',
    'order.shipped': 'Shipped',
    'order.delivered': 'Delivered',
    
    
    // === BASIC MESSAGES ===
    'message.success': 'Success!',
    'message.error': 'Error',
    'message.warning': 'Warning',
    'message.info': 'Information',
    'message.loading': 'Loading...',
    'message.no_results': 'No results found',
    'message.try_again': 'Try again',
    'message.connection_error': 'Connection error',
    'message.invalid_credentials': 'Invalid credentials',
    'message.account_created': 'Account created successfully',
    'message.login_success': 'Login successful',
    'message.logout_success': 'Logout successful',
    'message.product_added_to_cart': 'Product added to cart',
    'message.product_removed_from_cart': 'Product removed from cart',
    'message.order_placed': 'Order placed successfully',
    'message.payment_successful': 'Payment successful',
    
    // === BASIC FORMS ===
    'form.required': 'This field is required',
    'form.invalid_email': 'Invalid email',
    'form.password': 'Password',
    'form.password_too_short': 'Password is too short',
    'form.passwords_dont_match': 'Passwords do not match',
    'form.invalid_phone': 'Invalid phone number', 
    'form.terms_required': 'You must accept the terms and conditions',
    'form.first_name': 'First Name',
    'form.last_name': 'Last Name',
    'form.phone_or_email': 'Phone or Email',
    'form.phone_or_email_placeholder': 'Enter your email or phone number',
    
    // === BASIC FOOTER ===
    'footer.about_us': 'About Us',
    'footer.contact_us': 'Contact Us',
    'footer.customer_service': 'Customer Service',
    'footer.shipping_info': 'Shipping Information',
    'footer.return_policy': 'Return Policy',
    'footer.privacy_policy': 'Privacy Policy',
    'footer.terms_conditions': 'Terms and Conditions',
    'footer.follow_us': 'Follow Us',
    'footer.copyright': 'All rights reserved',
    'footer.text_colombian': 'Proudly Colombian',
    
    // === BASIC PAGES ===
    'page.home': 'Home',
    'page.about': 'About',
    'page.contact': 'Contact',
    'page.login': 'Login',
    'page.featured_products': 'Featured Products',
    'page.our_stores': 'Our Stores',
    'page.see_more': 'See more',
    'page.register': 'Register',
    'page.forgot_password': 'Forgot Password',
    'page.reset_password': 'Reset Password',
    'page.verify_email': 'Verify Email',
    'page.not_found': 'Page Not Found',
    'page.access_denied': 'Access Denied',
    'page.server_error': 'Server Error',
    'page.checkout': 'Checkout Process',
    'page.terms': 'Terms and Conditions',
    'page.privacy': 'Privacy Policy',
    'page.services': 'Services',
    'page.providers': 'Providers',
    'page.categories': 'Categories',
    'page.products': 'Products',
    'page.search_results': 'Search Results',
    'page.results': 'results',
    'page.results_for': 'Results for',
    'page.showing': 'Showing',
    
    // === BASIC FILTERS ===
    'filter.title': 'Filters',
    'filter.price_range': 'Price Range',
    'filter.brand': 'Brand',
    'filter.rating': 'Rating',
    'filter.availability': 'Availability',
    'filter.sort_by': 'Sort by',
    'filter.price_low_high': 'Price: Low to High',
    'filter.price_high_low': 'Price: High to Low',
    'filter.name_a_z': 'Name: A-Z',
    'filter.name_z_a': 'Name: Z-A',
    'filter.newest': 'Newest',
    'filter.popular': 'Most Popular',
    'filter.clear_all': 'Clear All',
    
    // === BASIC SELLERS ===
    'seller.title': 'Seller',
    'seller.shop': 'Shop',
    'seller.rating': 'Rating',
    'seller.reviews': 'Reviews',
    'seller.products': 'Products',
    'seller.contact': 'Contact',
    'seller.visit_shop': 'Visit Shop',
    'seller.about': 'About Seller',
     
    // === MODALS ===
    'modal.shipping_policy': 'Shipping Policy',
    'modal.add_address': 'Add Address',
    'modal.edit': 'Edit',
    'modal.delete': 'Delete',
    'modal.add': 'Add',
    'modal.save_address': 'Save Address',
  };

  constructor(private http: HttpClient) {
    this.initializeTranslations();
    this.loadSavedLanguage();
  }

  private initializeTranslations(): void {
    // Cargar traducciones base (español) inmediatamente
    this.translationsCache['es'] = this.baseTranslations;
    this.translationsSubject.next(this.baseTranslations);
  }

  private loadSavedLanguage(): void {
    const savedLang = localStorage.getItem('selectedLanguage');
    if (savedLang && (savedLang === 'es' || savedLang === 'en')) {
      this.setLanguage(savedLang);
    } else {
      this.setLanguage('es');
    }
  }

  getCurrentLanguage(): Observable<string> {
    return this.currentLanguage$;
  }

  getCurrentLanguageValue(): string {
    return this.currentLanguageSubject.value;
  }

  setLanguage(language: string): void {
    if (language === 'es' || language === 'en') {
      
      // Actualizar idioma actual
      this.currentLanguageSubject.next(language);
      
      // Guardar en localStorage
      localStorage.setItem('selectedLanguage', language);
      
      // Actualizar atributo lang del HTML
      document.documentElement.lang = language;
      
      // Cargar traducciones si no están en cache
      if (!this.translationsCache[language]) {
        this.loadTranslations(language);
      } else {
        // Notificar cambio de traducciones
        this.translationsSubject.next(this.translationsCache[language]);
      }
      
    }
  }

  private loadTranslations(language: string): void {
    if (language === 'en') {
      // Cargar traducciones en inglés desde memoria (rápido)
      this.translationsCache['en'] = this.englishTranslations;
      this.translationsSubject.next(this.englishTranslations);
    } else {
      // Cargar traducciones en español desde memoria (rápido)
      this.translationsCache['es'] = this.baseTranslations;
      this.translationsSubject.next(this.baseTranslations);
    }
  }

  translate(key: string, params?: { [key: string]: string | number }): string {
    const currentLang = this.currentLanguageSubject.value;
    let translation = this.translationsCache[currentLang]?.[key];
    
    // Si no encuentra la traducción, usar la de respaldo (español)
    if (!translation) {
      translation = this.baseTranslations[key];
    }
    
    // Si aún no encuentra, devolver la clave
    if (!translation) {
      console.warn(`Translation missing for key: ${key} in language: ${currentLang}`);
      return key;
    }

    // Reemplazar parámetros si existen
    if (params) {
      return this.replaceParams(translation, params);
    }

    return translation;
  }

  private replaceParams(text: string, params: { [key: string]: string | number }): string {
    let result = text;
    for (const [key, value] of Object.entries(params)) {
      result = result.replace(new RegExp(`{${key}}`, 'g'), String(value));
    }
    return result;
  }

  getAvailableLanguages(): { code: string; name: string; flag: string }[] {
    return [
      { code: 'es', name: 'Español', flag: '🇪🇸' },
      { code: 'en', name: 'English', flag: '🇺🇸' }
    ];
  }

  // Método para cargar traducciones adicionales desde archivos externos (opcional)
  loadAdditionalTranslations(language: string, section: string): Observable<{ [key: string]: string }> {
    return this.http.get<{ [key: string]: string }>(`/assets/i18n/${language}/${section}.json`)
      .pipe(
        catchError(() => of({})),
        map(translations => {
          // Fusionar con las traducciones existentes
          if (this.translationsCache[language]) {
            this.translationsCache[language] = { ...this.translationsCache[language], ...translations };
          } else {
            this.translationsCache[language] = translations;
          }
          
          // Actualizar si es el idioma actual
          if (this.currentLanguageSubject.value === language) {
            this.translationsSubject.next(this.translationsCache[language]);
          }
          
          return translations;
        })
      );
  }

  // Método para precargar traducciones
  preloadTranslations(language: string): void {
    if (!this.translationsCache[language]) {
      this.loadTranslations(language);
    }
  }

  // Método para obtener estadísticas de cache
  getCacheStats(): { [lang: string]: number } {
    const stats: { [lang: string]: number } = {};
    for (const [lang, translations] of Object.entries(this.translationsCache)) {
      stats[lang] = Object.keys(translations).length;
    }
    return stats;
  }
}
